import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, TrendingUpIcon, UsersIcon } from 'lucide-react';
export function CustomMetrics() {
  const navigate = useNavigate();
  return <div className="max-w-5xl mx-auto" data-id="element-5766">
      {/* Hero Section with Preview Card */}
      <div className="mb-12" data-id="element-5767">
        <div className="relative" data-id="element-5768">
          {/* Gradient Background Card */}
          <div className="bg-gradient-to-br from-blue-400 via-cyan-400 to-teal-400 rounded-2xl p-8 shadow-xl" data-id="element-5769">
            {/* Preview Dashboard Card */}
            <div className="bg-white rounded-xl shadow-lg p-6 max-w-md" data-id="element-5770">
              <div className="flex items-center justify-between mb-4" data-id="element-5771">
                <h3 className="text-lg font-semibold text-gray-900" data-id="element-5772">
                  Check daily
                </h3>
                <button className="text-sm text-gray-600 hover:text-gray-900" data-id="element-5773">
                  See all
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-6" data-id="element-5774">Shared</p>

              {/* Metric Cards */}
              <div className="space-y-4" data-id="element-5775">
                {/* Trial conversion rate */}
                <div className="flex items-center justify-between" data-id="element-5776">
                  <div data-id="element-5777">
                    <p className="text-xs text-gray-600 mb-1" data-id="element-5778">
                      Trial conversion rate
                    </p>
                    <div className="flex items-baseline gap-2" data-id="element-5779">
                      <span className="text-2xl font-bold text-gray-900" data-id="element-5780">
                        56.8%
                      </span>
                      <span className="text-xs text-green-600" data-id="element-5781">+3.1%</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-5782">
                    {/* Mini chart */}
                    <svg width="60" height="30" className="text-blue-500" data-id="element-5783">
                      <polyline points="0,25 10,20 20,22 30,15 40,18 50,10 60,8" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-5784" />
                    </svg>
                    <div className="text-right" data-id="element-5785">
                      <p className="text-xs text-gray-600" data-id="element-5786">High</p>
                      <p className="text-lg font-semibold text-gray-900" data-id="element-5787">598</p>
                      <p className="text-xs text-green-600" data-id="element-5788">+12</p>
                    </div>
                  </div>
                </div>

                {/* New customers */}
                <div className="flex items-center justify-between" data-id="element-5789">
                  <div data-id="element-5790">
                    <p className="text-xs text-gray-600 mb-1" data-id="element-5791">New customers</p>
                    <div className="flex items-baseline gap-2" data-id="element-5792">
                      <span className="text-2xl font-bold text-gray-900" data-id="element-5793">
                        1,624
                      </span>
                      <span className="text-xs text-green-600" data-id="element-5794">+4.5%</span>
                    </div>
                  </div>
                  <div className="flex gap-0.5" data-id="element-5795">
                    {/* Mini bar chart */}
                    {[12, 18, 15, 22, 19, 25, 20, 28, 24, 30, 26, 32].map((height, i) => <div key={i} className="w-1.5 bg-purple-500 rounded-sm" style={{
                    height: `${height}px`
                  }} data-id="element-5796" />)}
                  </div>
                </div>
              </div>
            </div>

            {/* Create new group popup */}
            <div className="absolute top-4 right-4 bg-white rounded-lg shadow-xl p-4 w-64" data-id="element-5797">
              <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-5798">
                Create a new group
              </h4>
              <div className="mb-3" data-id="element-5799">
                <label className="text-xs text-gray-600 mb-1 block" data-id="element-5800">Name</label>
                <input type="text" value="Growth reports" readOnly className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50" data-id="element-5801" />
              </div>
              <button className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-5802">
                <PlusIcon size={16} data-id="element-5803" />
                Add reports
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="text-center mb-8" data-id="element-5804">
        <h1 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-5805">
          Monitor your data with custom metrics
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-5806">
          Create custom metrics with Nadi to get quick answers to complex data
          questions. Organize and monitor them in one place.
        </p>
      </div>

      {/* CTA Button */}
      <div className="text-center" data-id="element-5807">
        <button onClick={() => navigate('/nadi')} className="inline-flex items-center px-6 py-3 text-base font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-colors" data-id="element-5808">
          Go to Nadi
        </button>
      </div>

      {/* Feature Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16" data-id="element-5809">
        <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-5810">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4" data-id="element-5811">
            <TrendingUpIcon size={24} className="text-blue-600" data-id="element-5812" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-5813">
            Track key metrics
          </h3>
          <p className="text-sm text-gray-600" data-id="element-5814">
            Monitor your most important business metrics in real-time with
            custom dashboards.
          </p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-5815">
          <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4" data-id="element-5816">
            <UsersIcon size={24} className="text-purple-600" data-id="element-5817" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-5818">
            Share insights
          </h3>
          <p className="text-sm text-gray-600" data-id="element-5819">
            Collaborate with your team by sharing custom metric groups and
            reports.
          </p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-5820">
          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4" data-id="element-5821">
            <PlusIcon size={24} className="text-green-600" data-id="element-5822" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-5823">
            Easy to create
          </h3>
          <p className="text-sm text-gray-600" data-id="element-5824">
            Build custom metrics with an intuitive interface - no SQL knowledge
            required.
          </p>
        </div>
      </div>
    </div>;
}